import { DateTime, LocaleOptions } from 'luxon'
import invariant from 'tiny-invariant'
import { match } from 'ts-pattern'

type FormatDateOptions = {
  date: string
  type:
    | 'mm/dd'
    | 'mm/dd/yyyy'
    | 'mm/dd/yy'
    | 'mm/dd/yyyy-hh:mm'
    | 'mm/dd/yyyy-hh:mm-ss'
    | 'time-with-zone'
    | 'mm/dd/yy-time-with-zone'
  locale?: LocaleOptions['locale']
}

export const formatDate = ({ date, type, locale = 'en-US' }: FormatDateOptions) => {
  const dateTime = DateTime.fromISO(date)
  invariant(dateTime.isValid, `dateTime is invalid: '${date}'`)

  return match(type)
    .with('mm/dd', () =>
      dateTime.toLocaleString(
        {
          month: 'numeric',
          day: 'numeric',
        },
        {
          locale,
        }
      )
    )
    .with('mm/dd/yyyy', () =>
      dateTime.toLocaleString(
        {
          month: 'numeric',
          day: 'numeric',
          year: 'numeric',
        },
        {
          locale,
        }
      )
    )
    .with('mm/dd/yy', () =>
      dateTime.toLocaleString(
        {
          month: 'numeric',
          day: 'numeric',
          year: '2-digit',
        },
        {
          locale,
        }
      )
    )
    .with('mm/dd/yyyy-hh:mm', () => dateTime.toLocal().toFormat('MM/dd/yyyy · hh:mm a', { locale }))
    .with('mm/dd/yyyy-hh:mm-ss', () => dateTime.toLocal().toFormat('MM/dd/yyyy - hh:mm:ss a', { locale }))
    .with('time-with-zone', () =>
      dateTime.toLocaleString(
        {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          timeZoneName: 'short',
        },
        {
          locale,
        }
      )
    )
    .with('mm/dd/yy-time-with-zone', () => dateTime.toFormat('M/d/yy hh:mm:ss a ZZZZ'))
    .exhaustive()
}

export const formatRelativeTime = (date: string): string => {
  const dateTime = DateTime.fromISO(date)
  invariant(dateTime.isValid, `dateTime is invalid: '${date}'`)

  return dateTime.toRelative()
}

/** The wall clock, in epoch milliseconds. */
export const nowMs = () => DateTime.now().toMillis()

/** An ISO instant in epoch milliseconds; `undefined`, and logged, when it does not parse. */
export const isoToMs = (iso: string) => {
  const dateTime = DateTime.fromISO(iso)
  if (!dateTime.isValid) {
    console.error(`timestamp is invalid: '${iso}'`)
    return undefined
  }
  return dateTime.toMillis()
}

export const msToIso = (ms: number) => {
  const iso = DateTime.fromMillis(ms).toISO()
  invariant(iso, `epoch milliseconds are invalid: '${ms}'`)
  return iso
}

/** `formatDuration`, never negative: an end that reads before the start (two clocks disagreeing) is no time at all. */
export const formatElapsed = (startIso: string, endIso: string) =>
  formatDuration(startIso, DateTime.fromISO(endIso) < DateTime.fromISO(startIso) ? startIso : endIso)

export const formatDuration = (startIso: string, endIso: string): string => {
  const start = DateTime.fromISO(startIso)
  const end = DateTime.fromISO(endIso)
  invariant(start.isValid, `start date is invalid: '${startIso}'`)
  invariant(end.isValid, `end date is invalid: '${endIso}'`)

  const diff = end.diff(start, ['hours', 'minutes', 'seconds']).mapUnits(Math.floor).rescale()
  return diff.toHuman({ unitDisplay: 'narrow' }).replace(/,\s*/g, ' ') || '0s'
}

/**
 * Whole days between `iso` and `now`, floored. `now` is a parameter rather than read from the clock so
 * callers that bucket by age stay deterministic under test.
 *
 * Returns `undefined` for an unparseable timestamp rather than asserting: the threat model's
 * `generatedAt` is optional on the wire, and one bad value should not take a whole estate roll-up down.
 */
export const daysSince = (iso: string, now: string): number | undefined => {
  const parsed = parseAgainstReference({ iso, now, reference: DateTime.fromISO(now) })
  return parsed && Math.floor(parsed.reference.diff(parsed.then, 'days').days)
}

/**
 * Parses `iso` for comparing against `reference`, or returns `undefined` when either will not parse.
 *
 * Logged the way `isIsoTimestampInPast` below logs the same condition: the value is still swallowed so
 * one bad timestamp cannot take a roll-up or a list down, but it should not vanish silently.
 *
 * @param now the text `reference` was parsed from, named in the log when it is the invalid one
 */
const parseAgainstReference = ({ iso, now, reference }: { iso: string; now?: string; reference: DateTime }) => {
  const then = DateTime.fromISO(iso)
  if (!then.isValid || !reference.isValid) {
    console.error(`timestamp is invalid: '${then.isValid ? now : iso}'`)
    return undefined
  }
  return { then, reference }
}

/** Past this, a week count stops being a useful sense of "how long ago" and a date reads better. */
const COMPACT_AGE_MAX_WEEKS = 13

/**
 * How long before `now` the `iso` timestamp was, in the narrowest unit that fits: "now", "2m", "1h",
 * "3d", "5w", and a short date ("Jan 5", or "Jan 5, 2025" in another year) past about three months.
 * `now` defaults to the current time; pass it to keep the result deterministic under test.
 *
 * Returns `undefined` for an unparseable timestamp, like `daysSince`, so one bad value blanks a cell
 * instead of taking a list down.
 */
export const formatCompactAge = (iso: string, now?: string): string | undefined => {
  // Unlike `daysSince`, the result depends on a zone: the short date and its same-year check read the
  // calendar in `reference`'s zone. `setZone` keeps the zone written in `now` (`Z` in tests), so a test
  // passing `now` renders the same date on every machine; without `now`, the reader's own zone applies.
  const reference = now === undefined ? DateTime.now() : DateTime.fromISO(now, { setZone: true })
  const parsed = parseAgainstReference({ iso, now, reference })
  if (!parsed) {
    return undefined
  }
  const { then } = parsed

  const { minutes } = reference.diff(then, 'minutes')
  if (minutes < 1) {
    return 'now'
  }
  if (minutes < 60) {
    return `${Math.floor(minutes)}m`
  }
  const hours = minutes / 60
  if (hours < 24) {
    return `${Math.floor(hours)}h`
  }
  const days = hours / 24
  if (days < 7) {
    return `${Math.floor(days)}d`
  }
  const weeks = days / 7
  if (weeks < COMPACT_AGE_MAX_WEEKS) {
    return `${Math.floor(weeks)}w`
  }

  const local = then.setZone(reference.zone)
  return local.toLocaleString(
    local.year === reference.year
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' },
    { locale: 'en-US' }
  )
}

export const isIsoTimestampInPast = (timestamp?: string) => {
  if (!timestamp) {
    return false
  }

  const dateTime = DateTime.fromISO(timestamp)
  if (!dateTime.isValid) {
    console.error(`timestamp is invalid: '${timestamp}'`)
    return false
  }

  return DateTime.now() > dateTime
}
